// @dx ds/domain — bespoke (non-factory) enum tags.
//
// These tags can't be produced by `createStaticTag` because they compose extra
// chrome: a status suffix, a leading glyph icon, a `+N` overflow tail, or an
// early return for a sentinel value. Split out of `EnumTag.tsx` so the factory-
// bound catalog module exports nothing but components.
import React from "react";
import { Tooltip } from "antd";
import { CheckOutlined, PieChartOutlined, ArrowUpOutlined, PauseCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Tag } from "@ds/display/Tag";
import { EmptyValue } from "@ds/display/EmptyValue";
import {
  DocumentType, DocumentStatus, ReviewStatus, CompanyCategory, ContactPurpose,
  EffectivePaymentState, CommentTag,
} from "@shared/types";
import {
  DOCUMENT_TYPE_COLORS, REVIEW_STATUS_COLORS, CATEGORY_COLORS,
  EFFECTIVE_PAYMENT_STATE_COLORS, EFFECTIVE_PAYMENT_STATE_GLYPHS,
  COMMENT_TAG_COLORS, PURPOSE_COLORS,
} from "@ds/tokens/domainTones";
import { createStaticTag, type StaticTagBaseProps } from "@ds/domain/EnumTag.factories";

// ============================================
// Document Tags
// ============================================

const BaseDocumentTypeTag = createStaticTag<DocumentType>(DOCUMENT_TYPE_COLORS, "documents.document_type");

/** Document type tag with optional status suffix: "CMR — kopia", "Aneks — oryginał". */
export const DocumentTypeTag: React.FC<StaticTagBaseProps & {
  value?: DocumentType | string | null;
  documentStatus?: DocumentStatus | null;
}> = ({ value, documentStatus, size = "md", testId }) => {
  const { t } = useTranslation();
  if (!value) return <EmptyValue />;
  if (!documentStatus) {
    return <BaseDocumentTypeTag value={value} size={size} testId={testId} />;
  }
  const statusLabel = t(`documents.document_status.${documentStatus}`);
  return (
    <Tag
      label={`${t(`documents.document_type.${value}`)} — ${statusLabel}`}
      tone={DOCUMENT_TYPE_COLORS[value as DocumentType] ?? "grey"}
      size={size}
      testId={testId}
    />
  );
};

export const ReviewStatusTag: React.FC<StaticTagBaseProps & { value?: ReviewStatus }> = ({
  value, size = "md", testId,
}) => {
  const { t } = useTranslation();
  if (!value || value === "not_applicable") return <EmptyValue />;
  return (
    <Tag
      label={t(`documents.review_status.${value}`)}
      tone={REVIEW_STATUS_COLORS[value] ?? "grey"}
      size={size}
      testId={testId}
    />
  );
};

// ============================================
// Comment Tags
// ============================================

export const CommentTagTag: React.FC<StaticTagBaseProps & { value?: CommentTag | null }> = ({
  value, size = "md", testId,
}) => {
  const { t } = useTranslation();
  if (!value) return null;
  return (
    <Tag
      label={t(`comments.tag.${value}`)}
      tone={COMMENT_TAG_COLORS[value] ?? "grey"}
      size={size}
      testId={testId}
    />
  );
};

// ============================================
// Company Tags
// ============================================

const CategoryTag = createStaticTag<CompanyCategory>(CATEGORY_COLORS, "companies.categories");

// Label abbreviations for compact mode (tooltip shows the full translated name).
const CATEGORY_INITIALS: Record<string, string> = {
  recycler: "R",
  transporter: "P",
  waste_generator: "W",
  exporter: "E",
  importer: "I",
  organizacja_odzysku: "O",
  commercial: "K",
  other: "?",
};

interface CategoriesTagsProps {
  values: CompanyCategory[];
  maxDisplay?: number;
  mode?: "full" | "compact";
}

export const CategoriesTags: React.FC<CategoriesTagsProps> = ({ values, maxDisplay = 3, mode = "full" }) => {
  const { t } = useTranslation();

  if (!values || values.length === 0) {
    return <EmptyValue />;
  }

  if (mode === "compact") {
    return (
      <>
        {values.map(value => (
          <Tooltip key={value} title={t(`companies.categories.${value}`, value)}>
            <Tag
              label={CATEGORY_INITIALS[value] ?? value.charAt(0).toUpperCase()}
              tone={CATEGORY_COLORS[value] ?? "grey"}
              size="sm"
            />
          </Tooltip>
        ))}
      </>
    );
  }

  const displayValues = values.slice(0, maxDisplay);
  const remaining = values.length - maxDisplay;

  return (
    <>
      {displayValues.map(value => (
        <CategoryTag key={value} value={value} />
      ))}
      {remaining > 0 && <Tag label={`+${remaining}`} tone="grey" size="sm" />}
    </>
  );
};

// ============================================
// Contact Tags
// ============================================

// Uses canonical PURPOSE_COLORS from domainTones (transport_notification→blue).
const ContactPurposeTag = createStaticTag<ContactPurpose>(PURPOSE_COLORS, "contacts.purposes");

interface ContactPurposesTagsProps {
  values: ContactPurpose[];
  maxDisplay?: number;
}

export const ContactPurposesTags: React.FC<ContactPurposesTagsProps> = ({ values, maxDisplay = 3 }) => {
  if (!values || values.length === 0) return <EmptyValue />;

  const displayValues = values.slice(0, maxDisplay);
  const remaining = values.length - maxDisplay;

  return (
    <>
      {displayValues.map(value => (
        <ContactPurposeTag key={value} value={value} />
      ))}
      {remaining > 0 && <Tag label={`+${remaining}`} tone="grey" size="sm" />}
    </>
  );
};

// ============================================
// Common Tags
// ============================================

export const BooleanTag: React.FC<StaticTagBaseProps & { value?: boolean }> = ({
  value, size = "md", testId,
}) => {
  const { t } = useTranslation();
  if (value === null || value === undefined) return <EmptyValue />;
  return (
    <Tag
      label={t(`common.boolean.${value ? "yes" : "no"}`)}
      tone={value ? "green" : "red"}
      size={size}
      testId={testId}
    />
  );
};

// ============================================
// Invoice Tags
// ============================================

/**
 * Tag for the derived effective payment state (computed from `mbank_invoice_payment_state`).
 * Bespoke (not factory-bound) — renders the canonical tone PLUS a leading glyph icon for
 * color-blind accessibility per the `EFFECTIVE_PAYMENT_STATE_GLYPHS` map.
 */
const EFFECTIVE_PAYMENT_STATE_ICONS: Record<string, React.ComponentType<{ style?: React.CSSProperties }>> = {
  CheckOutlined,
  PieChartOutlined,
  ArrowUpOutlined,
  PauseCircleOutlined,
};

export const EffectivePaymentStateTag: React.FC<StaticTagBaseProps & {
  value?: EffectivePaymentState | null;
}> = ({ value, size = "md", testId }) => {
  const { t } = useTranslation();
  if (!value) return <EmptyValue />;
  const glyphKey = EFFECTIVE_PAYMENT_STATE_GLYPHS[value];
  const Icon = glyphKey ? EFFECTIVE_PAYMENT_STATE_ICONS[glyphKey] : undefined;
  return (
    <Tag
      label={
        <>
          {Icon ? <Icon style={{ marginRight: 4 }} /> : null}
          {t(`invoices.payment_state.${value}`, value)}
        </>
      }
      tone={EFFECTIVE_PAYMENT_STATE_COLORS[value] ?? "grey"}
      size={size}
      testId={testId}
    />
  );
};
